import React, { useEffect, useRef } from "react";
import "./ParticleBackground.css";

const ParticleBackground = ({ children }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    // Detect mobile device
    const isMobile = window.innerWidth <= 768;
    const isSmallMobile = window.innerWidth <= 480;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Particle system - reduced count for mobile
    const particles = [];
    let particleCount = 150;

    // Reduce particles on mobile for better performance
    if (isSmallMobile) {
      particleCount = 30; // Very few particles on small phones
    } else if (isMobile) {
      particleCount = 60; // Reduced for tablets/larger phones
    }

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        // Slower movement on mobile
        const speedMultiplier = isMobile ? 0.3 : 0.8;
        this.vx = (Math.random() - 0.5) * speedMultiplier;
        this.vy = (Math.random() - 0.5) * speedMultiplier;
        // Smaller particles on mobile
        this.radius = isMobile
          ? Math.random() * 1.5 + 0.5
          : Math.random() * 2.5 + 1;
        this.opacity = Math.random() * 0.7 + 0.3;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);

        // Create hacking theme glow effect with matrix green
        const gradient = ctx.createRadialGradient(
          this.x,
          this.y,
          0,
          this.x,
          this.y,
          this.radius * 4
        );
        // Use hacking theme colors: Matrix green, cyan, neon green
        gradient.addColorStop(0, "#00ff9b"); // Primary green
        gradient.addColorStop(0.4, "#00e5ff"); // Cyan
        gradient.addColorStop(0.7, "#39ff14"); // Neon green
        gradient.addColorStop(1, "transparent");

        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.restore();
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Tailor connection density so lines remain visible on mobile without overdraw.
      const connectionDistance = isSmallMobile ? 70 : isMobile ? 90 : 120;
      const maxConnectionsPerParticle = isSmallMobile ? 2 : isMobile ? 4 : 6;
      const lineWidth = isSmallMobile ? 0.9 : isMobile ? 1.1 : 1.5;
      const maxLineAlpha = isSmallMobile ? 0.35 : 0.45;

      particles.forEach((particle, index) => {
        particle.update();
        particle.draw();

        let connections = 0;
        for (let j = index + 1; j < particles.length; j++) {
          if (connections >= maxConnectionsPerParticle) break;

          const otherParticle = particles[j];
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            connections += 1;
            ctx.save();
            ctx.globalAlpha =
              ((connectionDistance - distance) / connectionDistance) *
              maxLineAlpha;

            // Create gradient for hacking theme - green/cyan matrix effect
            const gradient = ctx.createLinearGradient(
              particle.x,
              particle.y,
              otherParticle.x,
              otherParticle.y
            );
            gradient.addColorStop(0, "#00ff9b"); // Primary green
            gradient.addColorStop(0.5, "#00e5ff"); // Cyan
            gradient.addColorStop(1, "#39ff14"); // Neon green

            ctx.strokeStyle = gradient;
            ctx.lineWidth = lineWidth;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
            ctx.restore();
          }
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="particle-container">
      <canvas ref={canvasRef} className="particle-background" />
      <div className="particle-content">{children}</div>
    </div>
  );
};

export default ParticleBackground;
